import React from "react";
import { buttonVariants } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { UnifiedModal } from "@/components/ui/unified-modal";
import { ROW_TEXT } from "@/lib/typography";

interface TagDeleteConfirmProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tagName: string;
  postCount: number;
  onConfirm: () => Promise<void>;
}

export function TagDeleteConfirm({ open, onOpenChange, tagName, postCount, onConfirm }: TagDeleteConfirmProps) {
  const [deleting, setDeleting] = React.useState(false);

  const handleConfirm = async () => {
    setDeleting(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch (error) {
      console.error("Delete failed:", error);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <UnifiedModal
      open={open}
      onOpenChange={onOpenChange}
      icon={<AlertTriangle />}
      title="Delete Tag?"
      description={`Are you sure you want to delete "${tagName}"?`}
      variant="destructive"
      stacked
      contentClassName="max-w-[90vw] sm:max-w-md"
    >
      <div className={cn(ROW_TEXT, "space-y-2 text-left")}>
        {postCount > 0 ? (
          <>
            <p>
              This will remove <strong>"{tagName}"</strong> from{" "}
              <strong>
                {postCount} post{postCount !== 1 ? "s" : ""}
              </strong>
              .
            </p>
          </>
        ) : (
          <p className="text-muted-foreground">This tag is not currently used on any posts.</p>
        )}
      </div>
      <div className="flex flex-row gap-3 mt-2">
        <button
          type="button"
          disabled={deleting}
          onClick={() => onOpenChange(false)}
          className={cn(buttonVariants({ variant: "secondary", size: "modal" }), "flex-1 m-0")}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={deleting}
          className={cn(buttonVariants({ variant: "destructive", size: "modal" }), "flex-1 m-0")}
        >
          {deleting ? "Deleting..." : "Delete Tag"}
        </button>
      </div>
    </UnifiedModal>
  );
}
